import { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

const VISITOR_KEY = "lo_visitor_id";
function getVisitorId() {
  let id = localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id = (crypto?.randomUUID?.() || `v-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
    localStorage.setItem(VISITOR_KEY, id);
  }
  return id;
}
function trackCartSnapshot(items) {
  const safeItems = (items || []).map((x) => ({
    key: x.key, name: x.name || x.title || x.label || x.key,
    universe: x.universe || "mixte", qty: Math.max(1, Math.min(10, Number(x.qty) || 1)),
    unit: Math.max(0, Number(x.unit) || 0),
  }));
  api.post("/analytics/cart", {
    visitor_id: getVisitorId(),
    path: window.location.pathname,
    items: safeItems,
  }).catch(() => {});
}

const CartCtx = createContext(null);
export const useCart = () => useContext(CartCtx);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("lo_cart") || "[]"); } catch { return []; }
  });
  const [promo, setPromo] = useState("");
  const [promoPercent, setPromoPercent] = useState(0);
  const [promoError, setPromoError] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);

  useEffect(() => { localStorage.setItem("lo_cart", JSON.stringify(items)); }, [items]);
  useEffect(() => {
    if (items.length) trackCartSnapshot(items);
  }, []);

  const add = (variant, qty = 1) => {
    const normalizedUniverse = variant.universe === "femme" ? "femme" : variant.universe === "homme" ? "homme" : variant.universe;
    // Un panier peut contenir Femme + Homme + Mixte.
    // L'univers final de la commande est déterminé côté serveur à partir des lignes réelles.
    setItems((prev) => {
      const i = prev.findIndex((x) => x.key === variant.key);
      const next = i >= 0
        ? prev.map((x, idx) => idx === i ? { ...x, qty: Math.min(10, x.qty + qty) } : x)
        : [...prev, { ...variant, universe: normalizedUniverse || "mixte", qty: Math.min(10, qty) }];
      trackCartSnapshot(next);
      return next;
    });
    return true;
  };
  const setQty = (key, n) => {
    setItems((prev) => {
      const next = n <= 0 ? prev.filter((x) => x.key !== key) : prev.map((x) => x.key === key ? { ...x, qty: Math.min(10, n) } : x);
      trackCartSnapshot(next);
      return next;
    });
  };
  const remove = (key) => setItems((prev) => { const next = prev.filter((x) => x.key !== key); trackCartSnapshot(next); return next; });
  const clear = () => { setItems((prev) => { trackCartSnapshot([]); return []; }); setPromo(""); setPromoPercent(0); setPromoError(""); };

  const applyPromo = async () => {
    const code = promo.trim();
    if (!code) { setPromoPercent(0); setPromoError(""); return; }
    setPromoBusy(true); setPromoError("");
    try {
      const { data } = await api.post("/promo/validate", { code });
      if (data.valid) { setPromoPercent(data.percent); setPromoError(""); }
      else { setPromoPercent(0); setPromoError("Code invalide ou expiré"); }
    } catch { setPromoPercent(0); setPromoError("Code invalide ou expiré"); }
    finally { setPromoBusy(false); }
  };

  const count = items.reduce((s, x) => s + x.qty, 0);
  const subtotal = +items.reduce((s, x) => s + x.unit * x.qty, 0).toFixed(2);
  const shipping = 0; // actual shipping is selected in checkout and calculated server-side
  const discount = +((subtotal * promoPercent) / 100).toFixed(2);
  const total = Math.max(0, +(subtotal - discount + shipping).toFixed(2));
  const promoApplied = promoPercent > 0 ? promo.trim().toUpperCase() : "";

  return (
    <CartCtx.Provider value={{ items, add, setQty, remove, clear, count, subtotal, shipping,
      discount, total, promo, setPromo, promoPercent, promoError, promoBusy, applyPromo, promoApplied }}>
      {children}
    </CartCtx.Provider>
  );
}
